import type { PptxAsset } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { isEmpty, isNull, xor } from "lodash";
import pluralize from "pluralize";
import React from "react";
import { useStore } from "zustand";

import { TELEMETRY_ID } from "../../../../constants/telemetry";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import {
    NotificationType,
    notificationStore,
} from "../../../../store/notification";
import { workbenchStore } from "../../../../store/workbench";
import type { DownloadAsset } from "../../../../types/common";
import { getAdvertiserId } from "../../../../utils/permission.utils";
import {
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
    logWithTakt,
} from "../../../../utils/telemetry.utils";
import { isWorkbenchActionDisabled } from "../../../../utils/workbench.utils";
import {
    AddedToGlobalWorkbenchNotification,
    AddedToWorkbenchNotification,
    UpdateGlobalWorkbenchNotification,
    UpdateToWorkbenchNotification,
} from "../../../common/notification.components";
import {
    ToolbarButtonGroup,
    ToolbarButton,
} from "../../../common/styled.components";
import { DynamicCartButton } from "../../../remote/DynamicCartButton";

interface WorkbenchActionsProps {
    title?: string;
    identifier: string;
    selectedSlides: Set<number>;
    setSelectedSlides: (selectedslides: Set<number>) => void;
    numPages: number;
    downloadAsset: DownloadAsset | undefined;
    previewAsset: DownloadAsset | undefined;
    insightType: InsightType;
    isCompliant: boolean;
    outputFileFormats?: string[];
}

export const WorkbenchActions: React.FunctionComponent<
    WorkbenchActionsProps
> = (props: WorkbenchActionsProps) => {
    const {
        title,
        identifier,
        selectedSlides,
        setSelectedSlides,
        numPages,
        downloadAsset,
        previewAsset,
        insightType,
        isCompliant,
        outputFileFormats,
    } = props;

    const advertiserId = getAdvertiserId();

    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const [pptxAssets, addPptxAsset, getSlideIndexes, getTotalSlides] =
        useStore(workbenchStore, (state) => [
            state.pptxAssets,
            state.addPptxAsset,
            state.getSlideIndexes,
            state.getTotalSlides,
        ]);

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const allSelected = selectedSlides.size === numPages;

    const handleAllSelect = () => {
        let newSelectedSlides = new Set<number>();
        if (!allSelected) {
            newSelectedSlides = new Set([...Array(numPages).keys()]);
        } else {
            newSelectedSlides = new Set<number>();
        }
        setSelectedSlides(newSelectedSlides);
    };

    const selectedSlidesHaveChanged: boolean =
        xor([...selectedSlides], [...getSlideIndexes(identifier)]).length === 0;

    const workbenchDisabled = isWorkbenchActionDisabled(
        isCompliant,
        previewAsset,
        downloadAsset,
        outputFileFormats,
    );

    const isAddToWorkbenchDisabled =
        workbenchDisabled ||
        (pptxAssets.has(identifier) && selectedSlidesHaveChanged) ||
        (isEmpty(selectedSlides) && selectedSlidesHaveChanged);

    const workbenchActionText = pptxAssets.has(identifier)
        ? selectedSlidesHaveChanged
            ? "Added to Workbench"
            : "Update Workbench"
        : isEmpty(selectedSlides)
          ? "Add to Workbench"
          : `Add ${pluralize("Slide", selectedSlides.size, true)} to Workbench`;

    const handleAddToWorkbench = (pptxAsset: PptxAsset) => {
        const isUpdate = pptxAssets.has(identifier);
        const slideCountChange =
            selectedSlides.size - getSlideIndexes(identifier).size;

        if (
            isEmpty(pptxAsset.content) ||
            isEmpty(pptxAsset.content.bucket) ||
            isEmpty(pptxAsset.content.key)
        ) {
            pptxAsset.contentUrl = downloadAsset?.downloadUrl;
            delete pptxAsset.content;
        }
        addPptxAsset({ ...pptxAsset });
        const filters = useUnifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();
        logWithTakt(TELEMETRY_ID.ADDED_TO_WB, {
            ...filters,
            identifier,
            insightType,
        });
        if (isUpdate) {
            upsertNotification(
                <UpdateToWorkbenchNotification
                    slideCountChange={slideCountChange}
                    totalSlidesInWorkbench={getTotalSlides()}
                />,
                NotificationType.SUCCESS,
                identifier,
            );
        } else {
            upsertNotification(
                <AddedToWorkbenchNotification
                    slidesAdded={selectedSlides.size}
                    totalSlidesInWorkbench={getTotalSlides()}
                />,
                NotificationType.SUCCESS,
                identifier,
            );
        }
    };

    return (
        <ToolbarButtonGroup>
            {!FeatureFlagsStore.getState().uxTreatmentsOnSidePanel && (
                <ToolbarButton
                    onClick={() => handleAllSelect()}
                    disabled={workbenchDisabled}
                    data-testid="workbench-actions-select-all-button"
                >
                    {allSelected ? "Unselect" : "Select"} All Slides
                </ToolbarButton>
            )}
            {FeatureFlagsStore.getState().globalWorkbench ? (
                <DynamicCartButton
                    identifier={identifier}
                    workbenchScope={advertiserId}
                    source={{
                        name: title,
                        slidesSelected: Array.from(selectedSlides),
                        assets: {
                            pptx: {
                                s3signedUrl: downloadAsset?.downloadUrl,
                            },
                            pdf: {
                                s3signedUrl: previewAsset?.downloadUrl,
                            },
                        },
                        origin: { clientId: "IHUB" },
                        deckMetadata: {
                            insightType,
                            insightIdentifier: identifier,
                            numPages,
                        },
                    }}
                    onSuccess={() => {
                        upsertNotification(
                            <AddedToGlobalWorkbenchNotification
                                slidesAdded={selectedSlides.size}
                            />,
                            NotificationType.INFO,
                            identifier,
                        );
                    }}
                    onUpdate={() => {
                        upsertNotification(
                            <UpdateGlobalWorkbenchNotification />,
                            NotificationType.INFO,
                            identifier,
                        );
                    }}
                    onSourceStateLoaded={(cartSourceData: any) => {
                        if (!isNull(cartSourceData)) {
                            props.setSelectedSlides(
                                new Set(cartSourceData.slidesSelected),
                            );
                        }
                    }}
                    disabled={workbenchDisabled}
                />
            ) : (
                <ToolbarButton
                    variant="primary"
                    onClick={() =>
                        void handleAddToWorkbench({
                            identifier,
                            content: downloadAsset?.content,
                            insightType,
                            previewUrl: previewAsset?.downloadUrl,
                            slideNumbers: [...selectedSlides].sort(),
                        })
                    }
                    disabled={isAddToWorkbenchDisabled}
                    data-testid="workbench-actions-workbench-button"
                >
                    {workbenchActionText}
                </ToolbarButton>
            )}
        </ToolbarButtonGroup>
    );
};
